import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatNum, formatMedida, unidadeLabel, formatDate } from "@/lib/format";
import { sharePdf, logAcesso } from "@/lib/share";
import { useLabelPrint } from "@/context/LabelPrintContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Printer, Pencil, Trash2, Undo2, Loader2, Search, MapPin, FileText, Share2, Clock, MoreHorizontal, Scissors } from "lucide-react";
import { toast } from "sonner";
import { RoloTimelineModal } from "@/components/RoloTimelineModal";
import { RomaneioDetalheModal } from "@/components/RomaneioDetalheModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUS_BADGE = {
  "Disponível": { bg: "#F0FDF4", color: "#16A34A" },
  "Utilizado": { bg: "#EFF6FF", color: "#2563EB" },
  "Excluído": { bg: "#F1F5F9", color: "#94A3B8" },
};

export default function Rolos() {
  const { hasPerm } = useAuth();
  const canPdf = hasPerm("estoque:pdf");
  const canReprint = hasPerm("estoque:reimprimir_etiqueta");
  const canMover = hasPerm("estoque:mover");
  const canEditar = hasPerm("estoque:editar");
  const canExcluir = hasPerm("estoque:excluir");
  const canEstornar = hasPerm("estoque:estornar");
  const canHistorico = hasPerm("estoque:historico");
  const { printLabel, printLabels } = useLabelPrint();
  const [rolos, setRolos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [combinacoes, setCombinacoes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [destinos, setDestinos] = useState([]);

  const [fForn, setFForn] = useState("");
  const [fTipo, setFTipo] = useState("");
  const [fCor, setFCor] = useState("");
  const [fStatus, setFStatus] = useState("Disponível");
  const [fQ, setFQ] = useState("");
  const [fLocal, setFLocal] = useState("");
  const [fData, setFData] = useState(""); // "" | "hoje" | "periodo"
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");
  const [fDevolucao, setFDevolucao] = useState(false);
  const [fPedaco, setFPedaco] = useState(false);
  const [selecionados, setSelecionados] = useState([]);

  const [mover, setMover] = useState(null);
  const [movLocal, setMovLocal] = useState("");
  const [movMotivo, setMovMotivo] = useState("");

  const [editRolo, setEditRolo] = useState(null);
  const [edPeso, setEdPeso] = useState("");
  const [edNf, setEdNf] = useState("");
  const [edObs, setEdObs] = useState("");
  const [edDataEtq, setEdDataEtq] = useState("");
  const [edComb, setEdComb] = useState("");
  const [edRef, setEdRef] = useState("");
  const [edDestino, setEdDestino] = useState("");
  const [edMotivo, setEdMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState(null); // {tipo:'excluir'|'estornar', rolo}
  const [confirmMotivo, setConfirmMotivo] = useState("");
  const [ordem, setOrdem] = useState("data_desc");
  const [timelineRoloId, setTimelineRoloId] = useState(null);
  const [openRomaneioIdModal, setOpenRomaneioIdModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (fForn) params.fornecedor_id = fForn;
    if (fTipo) params.tipo = fTipo;
    if (fCor) params.cor = fCor;
    if (fStatus) params.status = fStatus;
    if (fQ) params.q = fQ;
    if (fLocal) params.local = fLocal;
    api
      .get("/rolos", { params })
      .then((r) => setRolos(r.data))
      .catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);})
      .finally(() => setLoading(false));
  }, [fForn, fTipo, fCor, fStatus, fQ, fLocal]);

  useEffect(() => {
    api.get("/combinacoes").then((r) => setCombinacoes(r.data)).catch(() => {});
    api.get("/fornecedores").then((r) => setFornecedores(r.data)).catch(() => {});
    api.get("/destinos").then((r) => setDestinos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tiposUnicos = useMemo(
    () => [...new Set(combinacoes.map((c) => c.tipo))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [combinacoes]
  );
  const coresUnicas = useMemo(
    () => [...new Set(combinacoes.map((c) => c.cor))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [combinacoes]
  );

  // filtro de data de entrada (no cliente, sobre o que já veio do backend)
  const ymdLocal = (iso) => {
    const x = new Date(iso);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  };
  const rolosExibidos = useMemo(() => {
    let base = rolos;
    if (fData === "hoje") {
      const hoje = ymdLocal(new Date().toISOString());
      base = rolos.filter((r) => r.data_entrada && ymdLocal(r.data_entrada) === hoje);
    } else if (fData === "periodo") {
      base = rolos.filter((r) => {
        if (!r.data_entrada) return false;
        const d = ymdLocal(r.data_entrada);
        if (dataDe && d < dataDe) return false;
        if (dataAte && d > dataAte) return false;
        return true;
      });
    }
    if (fDevolucao) {
      base = base.filter((r) => r.origem === "Devolução de Sobra");
    }
    if (fPedaco) {
      base = base.filter((r) => r.is_pedaco === true);
    }
    const peso = (r) => Number(r.peso_atual_kg || 0);
    const data = (r) => new Date(r.data_entrada || 0).getTime();
    const arr = [...base];
    arr.sort((a, b) => {
      switch (ordem) {
        case "data_asc":
          return data(a) - data(b);
        case "peso_desc":
          return peso(b) - peso(a);
        case "peso_asc":
          return peso(a) - peso(b);
        case "cor_az":
          return `${a.cor}`.localeCompare(`${b.cor}`, "pt-BR") || data(b) - data(a);
        case "tipocor_az":
          return `${a.tipo} ${a.cor}`.localeCompare(`${b.tipo} ${b.cor}`, "pt-BR");
        default:
          return data(b) - data(a);
      }
    });
    return arr;
  }, [rolos, fData, dataDe, dataAte, fDevolucao, fPedaco, ordem]);

  // consolidado dos rolos filtrados (totais por unidade)
  const consolidado = useMemo(() => {
    const visiveis = rolosExibidos.filter((r) => r.status !== "Excluído");
    let kg = 0;
    let m = 0;
    visiveis.forEach((r) => {
      if (r.unidade === "m") m += Number(r.peso_atual_kg || 0);
      else kg += Number(r.peso_atual_kg || 0);
    });
    return { rolos: visiveis.length, kg, m };
  }, [rolosExibidos]);

  // agrega rolos filtrados por tecido para o relatório

  // seleção para impressão em lote
  const toggleSel = (id) =>
    setSelecionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const idsVisiveis = useMemo(() => rolosExibidos.map((r) => r.id), [rolosExibidos]);
  const todosSelecionados = idsVisiveis.length > 0 && idsVisiveis.every((id) => selecionados.includes(id));
  const toggleTodos = () =>
    setSelecionados(todosSelecionados ? selecionados.filter((id) => !idsVisiveis.includes(id)) : [...new Set([...selecionados, ...idsVisiveis])]);
  const qtdSelVisiveis = idsVisiveis.filter((id) => selecionados.includes(id)).length;
  const imprimirSelecionadas = () => {
    const arr = rolosExibidos.filter((r) => selecionados.includes(r.id));
    if (arr.length === 0) return toast.error("Marque ao menos um rolo");
    printLabels(arr);
  };
  const imprimirTodosFiltrados = () => {
    if (rolosExibidos.length === 0) return toast.error("Nenhum rolo no filtro atual");
    printLabels(rolosExibidos);
  };

  const reportParams = () => {
    const p = new URLSearchParams();
    if (fForn) p.set("fornecedor_id", fForn);
    if (fTipo) p.set("tipo", fTipo);
    if (fCor) p.set("cor", fCor);
    p.set("mostrar", "todos");
    return p.toString();
  };
  const imprimirRelatorio = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/relatorio-estoque/pdf?${reportParams()}`, "_blank");
    logAcesso({ tipo: "estoque_relatorio_impresso", documento_tipo: "estoque" });
  };
  const compartilharRelatorio = async () => {
    await sharePdf(`/relatorio-estoque/pdf?${reportParams()}`, "Relatorio-Estoque.pdf", "Relatório de Estoque");
    logAcesso({ tipo: "estoque_relatorio_compartilhado", documento_tipo: "estoque" });
  };

  const filtroTexto = [
    fForn && `Fornecedor: ${fornecedores.find((f) => f.id === fForn)?.nome || ""}`,
    fTipo && `Tipo: ${fTipo}`,
    fCor && `Cor: ${fCor}`,
    fStatus && `Status: ${fStatus}`,
    fLocal && `Local: ${fLocal}`,
    fDevolucao && "Somente devoluções",
  ]
    .filter(Boolean)
    .join(" · ") || "Todos os rolos";

  const openEdit = (rolo) => {
    setEditRolo(rolo);
    setEdPeso(String(rolo.peso_atual_kg).replace(".", ","));
    setEdNf(rolo.nota_fiscal || "");
    setEdObs(rolo.observacao || "");
    setEdDataEtq(rolo.data_entrada_manual || "");
    setEdComb(rolo.combinacao_id);
    setEdRef(rolo.ref_saida || "");
    setEdDestino(rolo.destino_saida || "");
    setEdMotivo("");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      if (editRolo.status === "Utilizado") {
        await api.put(`/rolos/${editRolo.id}/saida`, {
          ref_vinculada: edRef,
          destino: edDestino,
          motivo: edMotivo,
        });
      } else {
        const p = parseFloat(String(edPeso).replace(",", "."));
        await api.put(`/rolos/${editRolo.id}`, {
          peso_kg: p,
          nota_fiscal: edNf,
          combinacao_id: edComb,
          data_entrada_manual: edDataEtq,
          observacao: edObs,
          motivo: edMotivo,
        });
      }
      toast.success(`${editRolo.codigo} atualizado`);
      setEditRolo(null);
      load();
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const doConfirm = async () => {
    const { tipo, rolo } = confirm;
    setSaving(true);
    try {
      if (tipo === "excluir") {
        await api.delete(`/rolos/${rolo.id}`, {
          params: { motivo: confirmMotivo || "Exclusão de lançamento" },
        });
        toast.success(`${rolo.codigo} excluído`);
      } else {
        await api.post(`/rolos/${rolo.id}/estorno`, null, {
          params: { motivo: confirmMotivo || "Estorno de saída" },
        });
        toast.success(`${rolo.codigo} estornado — voltou para o estoque`);
      }
      setConfirm(null);
      setConfirmMotivo("");
      load();
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const openMover = (rolo) => {
    setMover(rolo);
    setMovLocal(rolo.localizacao || "");
    setMovMotivo("");
  };

  const doMover = async () => {
    setSaving(true);
    try {
      await api.put(`/rolos/${mover.id}/localizacao`, { localizacao: movLocal, motivo: movMotivo });
      toast.success(`${mover.codigo} movido para ${movLocal}`);
      setMover(null);
      load();
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "h-11 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div data-testid="rolos-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Estoque</h1>
          <p className="mt-1 text-sm text-slate-500">
            Visualize, filtre, edite, estorne, mova e reimprima etiquetas de todos os rolos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canPdf && (
          <button
            onClick={imprimirRelatorio}
            data-testid="estoque-report"
            className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            <FileText size={18} /> Relatório PDF
          </button>
          )}
          {canPdf && (
          <button
            onClick={compartilharRelatorio}
            data-testid="estoque-report-share"
            className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Share2 size={18} /> Compartilhar
          </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <select className={inputCls} value={fForn} onChange={(e) => setFForn(e.target.value)} data-testid="rolos-filtro-fornecedor">
          <option value="">Todos os fornecedores</option>
          {fornecedores.map((f) => (
            <option key={f.id} value={f.id}>{f.nome}</option>
          ))}
        </select>
        <select className={inputCls} value={fTipo} onChange={(e) => setFTipo(e.target.value)} data-testid="rolos-filtro-tipo">
          <option value="">Todos os tipos</option>
          {tiposUnicos.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select className={inputCls} value={fCor} onChange={(e) => setFCor(e.target.value)} data-testid="rolos-filtro-cor">
          <option value="">Todas as cores</option>
          {coresUnicas.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select className={inputCls} value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="rolos-filtro-status">
          <option value="">Todos (exceto excluídos)</option>
          <option value="Disponível">Disponível</option>
          <option value="Utilizado">Utilizado</option>
          <option value="Excluído">Excluído</option>
        </select>
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${inputCls} pl-10 font-mono`} placeholder="Buscar código (ROLO-...)" value={fQ} onChange={(e) => setFQ(e.target.value)} data-testid="rolos-busca" />
        </div>
        <div className="relative">
          <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${inputCls} pl-10`} placeholder="Buscar localização (ex: 2-E)" value={fLocal} onChange={(e) => setFLocal(e.target.value)} data-testid="rolos-filtro-local" />
        </div>
        <select className={inputCls} value={fData} onChange={(e) => setFData(e.target.value)} data-testid="rolos-filtro-data">
          <option value="">Qualquer data de entrada</option>
          <option value="hoje">Entradas de hoje</option>
          <option value="periodo">Escolher período…</option>
        </select>
        {fData === "periodo" && (
          <>
            <input type="date" className={inputCls} value={dataDe} onChange={(e) => setDataDe(e.target.value)} data-testid="rolos-data-de" title="De" />
            <input type="date" className={inputCls} value={dataAte} onChange={(e) => setDataAte(e.target.value)} data-testid="rolos-data-ate" title="Até" />
          </>
        )}
        <select className={inputCls} value={ordem} onChange={(e) => setOrdem(e.target.value)} data-testid="rolos-ordenar" title="Ordenar">
          <option value="data_desc">Mais recente primeiro</option>
          <option value="data_asc">Mais antigo primeiro</option>
          <option value="peso_desc">Maior peso primeiro</option>
          <option value="peso_asc">Menor peso primeiro</option>
          <option value="cor_az">Cor (A→Z)</option>
          <option value="tipocor_az">Tipo/Cor (A→Z)</option>
        </select>
        <label
          className={`flex h-12 cursor-pointer items-center gap-2 rounded-md border-2 px-3 text-sm font-semibold transition-colors ${
            fDevolucao ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-100"
          }`}
          data-testid="rolos-filtro-devolucao-label"
          title="Mostrar somente rolos que vieram de devolução"
        >
          <input
            type="checkbox"
            checked={fDevolucao}
            onChange={(e) => setFDevolucao(e.target.checked)}
            data-testid="rolos-filtro-devolucao"
            className="h-4 w-4 accent-slate-900"
          />
          <Undo2 size={16} />
          Só devoluções
        </label>
        <label
          className={`flex h-12 cursor-pointer items-center gap-2 rounded-md border-2 px-3 text-sm font-semibold transition-colors ${
            fPedaco ? "border-amber-600 bg-amber-600 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-100"
          }`}
          data-testid="rolos-filtro-pedaco-label"
          title="Mostrar somente rolos classificados como pedaço (abaixo do limite do tipo)"
        >
          <input
            type="checkbox"
            checked={fPedaco}
            onChange={(e) => setFPedaco(e.target.checked)}
            data-testid="rolos-filtro-pedaco"
            className="h-4 w-4 accent-amber-600"
          />
          <Scissors size={16} />
          Só pedaços
        </label>
      </div>

      {/* Consolidado do filtro */}
      <div className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md bg-slate-900 px-5 py-4 text-white" data-testid="estoque-consolidado">
        <div>
          <span className="text-3xl font-bold font-mono">{consolidado.rolos}</span>
          <span className="ml-2 text-sm font-medium text-slate-300">rolos</span>
        </div>
        {consolidado.kg > 0 && (
          <div>
            <span className="text-3xl font-bold font-mono">{formatNum(consolidado.kg)}</span>
            <span className="ml-2 text-sm font-medium text-slate-300">kg</span>
          </div>
        )}
        {consolidado.m > 0 && (
          <div>
            <span className="text-3xl font-bold font-mono">{formatNum(consolidado.m)}</span>
            <span className="ml-2 text-sm font-medium text-slate-300">m</span>
          </div>
        )}
        <div className="ml-auto text-xs text-slate-400">{filtroTexto}</div>
      </div>

      {/* Barra de impressão de etiquetas em lote */}
      {!loading && rolosExibidos.length > 0 && canReprint && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border-2 border-slate-200 bg-slate-50 px-4 py-3" data-testid="lote-toolbar">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={todosSelecionados}
              onChange={toggleTodos}
              data-testid="lote-marcar-todos"
              className="h-4 w-4 accent-slate-900"
            />
            Marcar todos ({qtdSelVisiveis}/{rolosExibidos.length})
          </label>
          <button
            onClick={imprimirSelecionadas}
            data-testid="lote-imprimir-selecionadas"
            className="flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"
          >
            <Printer size={16} /> Imprimir selecionadas ({qtdSelVisiveis})
          </button>
          <button
            onClick={imprimirTodosFiltrados}
            data-testid="lote-imprimir-todos"
            className="flex items-center gap-2 rounded-md border-2 border-slate-900 bg-white px-3 py-2 text-sm font-bold text-slate-900 hover:bg-slate-100"
          >
            <Printer size={16} /> Imprimir etiquetas de todos os filtrados ({rolosExibidos.length})
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20 text-slate-400">
          <Loader2 className="animate-spin" />
        </div>
      ) : rolosExibidos.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="rolos-empty">
          Nenhum rolo encontrado com esses filtros.
        </div>
      ) : (
        <div className="space-y-2" data-testid="rolos-list">
          {rolosExibidos.map((r) => {
            const badge = STATUS_BADGE[r.status] || STATUS_BADGE["Excluído"];
            const excluido = r.status === "Excluído";
            return (
              <div
                key={r.id}
                className="flex flex-col gap-3 rounded-md border-2 border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                data-testid={`rolo-row-${r.codigo}`}
              >
                <div className="flex min-w-0 items-start gap-3">
                  {canReprint && (
                  <input
                    type="checkbox"
                    checked={selecionados.includes(r.id)}
                    onChange={() => toggleSel(r.id)}
                    data-testid={`lote-check-${r.codigo}`}
                    className="mt-1 h-4 w-4 shrink-0 accent-slate-900"
                  />
                  )}
                  <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`font-mono font-bold ${excluido ? "text-slate-400 line-through" : "text-slate-900"}`}>
                      {r.codigo}
                    </span>
                    <span className="rounded-sm px-2 py-0.5 text-xs font-bold" style={{ background: badge.bg, color: badge.color }}>
                      {r.status}
                    </span>
                    {r.origem === "Devolução de Sobra" && (
                      <span
                        className="flex items-center gap-1 rounded-sm bg-slate-900 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-white"
                        data-testid={`rolo-badge-devolucao-${r.codigo}`}
                        title={r.ref_origem ? `Devolução da REF ${r.ref_origem}` : "Devolução"}
                      >
                        <Undo2 size={12} /> Devolução{r.ref_origem ? ` · REF ${r.ref_origem}` : ""}
                      </span>
                    )}
                    {r.is_pedaco === true && (
                      <span
                        className="flex items-center gap-1 rounded-sm bg-amber-500 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-white"
                        data-testid={`rolo-badge-pedaco-${r.codigo}`}
                        title={r.pedaco_limite ? `Abaixo do limite (${r.pedaco_limite} ${r.unidade || "kg"}) do tipo ${r.tipo}` : "Pedaço"}
                      >
                        <Scissors size={12} /> Pedaço
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-sm text-slate-500">
                    {r.tipo} {r.cor} · {r.fornecedor_nome} · <span className="font-mono font-semibold">{formatMedida(r.peso_atual_kg, r.unidade)}</span>
                    {r.ref_saida ? ` · ${r.ref_saida}` : ""}
                    {r.status === "Disponível" ? ` · entrada ${formatDate(r.data_entrada)}` : ""}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-sm font-medium text-slate-600">
                    <MapPin size={14} className="text-slate-400" />
                    {r.localizacao || <span className="text-slate-400">sem localização</span>}
                  </div>
                </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {canReprint && (
                  <button
                    onClick={() => { printLabel(r); logAcesso({ tipo: "etiqueta_reimpressa", documento_tipo: "rolo", documento_id: r.id, documento_codigo: r.codigo }); }}
                    data-testid={`reprint-${r.codigo}`}
                    className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <Printer size={16} /> Etiqueta
                  </button>
                  )}

                  {(canHistorico || (!excluido && (canMover || canEditar)) || (r.status === "Disponível" && canExcluir) || (r.status === "Utilizado" && canEstornar)) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`acoes-${r.codigo}`}
                        className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                        aria-label="Mais ações"
                      >
                        <MoreHorizontal size={16} /> Mais
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {canHistorico && (
                      <DropdownMenuItem
                        onSelect={() => setTimelineRoloId(r.id)}
                        data-testid={`historico-${r.codigo}`}
                        className="gap-2 py-2 text-sm"
                      >
                        <Clock size={16} /> Histórico do rolo
                      </DropdownMenuItem>
                      )}

                      {!excluido && canMover && (
                        <DropdownMenuItem
                          onSelect={() => openMover(r)}
                          data-testid={`mover-${r.codigo}`}
                          className="gap-2 py-2 text-sm text-blue-700"
                        >
                          <MapPin size={16} /> Mover
                        </DropdownMenuItem>
                      )}

                      {!excluido && canEditar && (
                        <DropdownMenuItem
                          onSelect={() => openEdit(r)}
                          data-testid={`edit-${r.codigo}`}
                          className="gap-2 py-2 text-sm"
                        >
                          <Pencil size={16} /> Editar
                        </DropdownMenuItem>
                      )}

                      {r.status === "Disponível" && canExcluir && (
                        <DropdownMenuItem
                          onSelect={() => setConfirm({ tipo: "excluir", rolo: r })}
                          data-testid={`delete-${r.codigo}`}
                          className="gap-2 py-2 text-sm text-red-600 focus:bg-red-50 focus:text-red-700"
                        >
                          <Trash2 size={16} /> Excluir
                        </DropdownMenuItem>
                      )}

                      {r.status === "Utilizado" && canEstornar && (
                        <DropdownMenuItem
                          onSelect={() => setConfirm({ tipo: "estornar", rolo: r })}
                          data-testid={`estorno-${r.codigo}`}
                          className="gap-2 py-2 text-sm text-amber-700 focus:bg-amber-50 focus:text-amber-800"
                        >
                          <Undo2 size={16} /> Estornar
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog de edição */}
      <Dialog open={!!editRolo} onOpenChange={(o) => !o && setEditRolo(null)}>
        <DialogContent data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>
              Editar {editRolo?.codigo} {editRolo?.status === "Utilizado" ? "(saída)" : ""}
            </DialogTitle>
          </DialogHeader>

          {editRolo?.status === "Utilizado" ? (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Referência (REF)</label>
                <input className={inputCls} value={edRef} onChange={(e) => setEdRef(e.target.value)} data-testid="edit-ref" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Destino</label>
                <select className={inputCls} value={edDestino} onChange={(e) => setEdDestino(e.target.value)} data-testid="edit-destino">
                  {destinos.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">
                  {editRolo?.unidade === "m" ? "Metragem (m)" : "Peso (kg)"}
                </label>
                <input className={inputCls} value={edPeso} onChange={(e) => setEdPeso(e.target.value)} data-testid="edit-peso" inputMode="decimal" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Tecido</label>
                <select className={inputCls} value={edComb} onChange={(e) => setEdComb(e.target.value)} data-testid="edit-combinacao">
                  {combinacoes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.tipo} {c.cor} — {c.fornecedor_nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Nota Fiscal</label>
                <input className={inputCls} value={edNf} onChange={(e) => setEdNf(e.target.value)} data-testid="edit-nf" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Data de entrada (sai na etiqueta — opcional)</label>
                <input type="date" className={inputCls} value={edDataEtq} onChange={(e) => setEdDataEtq(e.target.value)} data-testid="edit-data-etiqueta" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Observação (sai na etiqueta — opcional)</label>
                <input className={inputCls} value={edObs} onChange={(e) => setEdObs(e.target.value)} placeholder="ex: rolo com ponta emendada" data-testid="edit-observacao" />
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Motivo da correção (opcional)</label>
            <input className={inputCls} value={edMotivo} onChange={(e) => setEdMotivo(e.target.value)} placeholder="ex: peso digitado errado" data-testid="edit-motivo" />
          </div>

          <DialogFooter>
            <button
              onClick={saveEdit}
              disabled={saving}
              data-testid="edit-save"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="animate-spin" size={18} />} Salvar correção
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de mover localização */}
      <Dialog open={!!mover} onOpenChange={(o) => !o && setMover(null)}>
        <DialogContent data-testid="mover-dialog">
          <DialogHeader>
            <DialogTitle>Mover {mover?.codigo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
              Localização atual: <strong>{mover?.localizacao || "(sem local)"}</strong>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Nova localização</label>
              <input className={inputCls} value={movLocal} onChange={(e) => setMovLocal(e.target.value)} placeholder="ex: 3-D (3º andar, direita)" data-testid="mover-local" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Motivo (opcional)</label>
              <input className={inputCls} value={movMotivo} onChange={(e) => setMovMotivo(e.target.value)} placeholder="ex: reorganização" data-testid="mover-motivo" />
            </div>
            <p className="text-xs text-slate-400">A mudança fica registrada no histórico (quem moveu, quando, de onde → para onde).</p>
          </div>
          <DialogFooter>
            <button
              onClick={doMover}
              disabled={saving}
              data-testid="mover-save"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="animate-spin" size={18} />} Salvar localização
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação excluir / estornar */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.tipo === "excluir" ? "Excluir rolo" : "Estornar saída"} — {confirm?.rolo?.codigo}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.tipo === "excluir"
                ? "O rolo sairá do estoque mas permanece no histórico (status Excluído)."
                : "O rolo voltará para o estoque como Disponível. A ação fica registrada no histórico."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            className={inputCls}
            value={confirmMotivo}
            onChange={(e) => setConfirmMotivo(e.target.value)}
            placeholder="Motivo (opcional)"
            data-testid="confirm-motivo"
          />
          <AlertDialogFooter>
            <button
              onClick={() => setConfirm(null)}
              className="rounded-md border-2 border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              Cancelar
            </button>
            <button
              onClick={doConfirm}
              disabled={saving}
              data-testid="confirm-ok"
              className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="animate-spin" size={16} />}
              {confirm?.tipo === "excluir" ? "Excluir" : "Estornar"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RoloTimelineModal
        roloId={timelineRoloId}
        open={!!timelineRoloId}
        onClose={() => setTimelineRoloId(null)}
        onOpenRomaneio={(rom) => setOpenRomaneioIdModal(rom.id)}
      />
      <RomaneioDetalheModal
        romaneioId={openRomaneioIdModal}
        open={!!openRomaneioIdModal}
        onClose={() => setOpenRomaneioIdModal(null)}
        zClass="z-[60]"
      />
    </div>
  );
}
